/**
 * useDestinationList
 *
 * Fetches a provider's destinations (Slack channels / Google Chat spaces) for a
 * credential via React Query, keyed on (provider, action, credential). The list
 * is cached and deduped: re-opening a routine's editor reuses the cached result
 * (the global 5-min staleTime) instead of re-hitting Slack / Google Chat every
 * time, and concurrent identical requests are coalesced. `setItems` patches the
 * cached list in place for the optimistic Slack-invite update.
 *
 * Used by the per-routine `RoutineChannelPicker` (one instance per provider) so
 * the fetch/extract/error semantics live in ONE place.
 */

import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@strivelabs/ui-core';
import { executeAppAction, BASE_URL } from '@/services/DataService';

export function useDestinationList<T>(
  credId: string,
  appName: string,
  action: string,
  extract: (data: unknown) => T[],
  errorNoun: string,
) {
  const queryClient = useQueryClient();
  const queryKey = ['routine-destinations', appName, action, credId];

  const { data, isLoading, error } = useQuery({
    queryKey,
    // No credential yet → nothing to fetch (and no spinner).
    enabled: Boolean(credId),
    queryFn: async (): Promise<T[]> => {
      const response = (await executeAppAction(
        BASE_URL, appName, action, [{ id: credId }], [{}],
      )) as { items?: Array<{ data?: unknown }> };
      return extract(response?.items?.[0]?.data);
    },
  });

  // React Query v5 has no per-query onError; surface a fetch failure as a toast.
  // The list stays empty and the other provider's section is unaffected.
  useEffect(() => {
    if (!error) return;
    console.error(`Error fetching ${errorNoun}:`, error);
    toast({
      title: `Failed to fetch ${errorNoun}`,
      description: error instanceof Error ? error.message : 'An error occurred',
      variant: 'destructive',
    });
  }, [error, errorNoun]);

  // Optimistic patch for the Slack-invite flow: rewrite the cached list in place
  // (e.g. flip is_member after a join succeeds) without a refetch.
  const setItems = useCallback(
    (updater: (prev: T[]) => T[]) => {
      queryClient.setQueryData<T[]>(
        ['routine-destinations', appName, action, credId],
        (prev) => updater(prev ?? []),
      );
    },
    [queryClient, appName, action, credId],
  );

  return { items: data ?? [], isLoading, setItems };
}
